import type { Phase, WakeProgram } from './types'
import { DEFAULT_DAY, NIGHT_END_MIN, dayStartOf, nextDayStart, type DaySettings } from './day'

/**
 * Несколько окон бодрствования на день (просьба заказчика 27.10.2026).
 *
 * Сомнологи строят день так: окна растут к вечеру — 1:35, 1:45, 1:50, 2:00… Одной программы
 * на все окна мало. ВБ 1 — это прежний «Мой день» (рекомендованная программа с правками),
 * ВБ 2…N — свои списки фаз, каждый со своим именем. Какое ВБ идёт, решает счёт окон с
 * начала дня: первое окно после подъёма — ВБ 1, после первого сна — ВБ 2, и так далее.
 * Ночные пробуждения не считаются. Когда внесённые ВБ кончились, берётся окно по возрасту.
 *
 * Чистые функции: та же логика повторена на Kotlin для оболочки и часов.
 */

export interface WakeSchedule {
  /** Имя карточки: «ВБ 2» по умолчанию, человек может переименовать. */
  name: string
  phases: Phase[]
  /**
   * Сколько должен длиться сон перед этим ВБ, минуты (то есть сон после предыдущего ВБ).
   * Так все сны дня лежат в этом же списке: сон после ВБ 1 — на карточке ВБ 2, и далее;
   * после последнего ВБ — ночь, ей длительность не нужна. Нет — подсказки нет.
   */
  napBefore?: number
}

const МИН = 60_000
const DAY = 24 * 3600 * 1000

/** Сколько всего ВБ в графике: ВБ 1 плюс добавленные. */
export function scheduleCount(extra: WakeSchedule[]): number {
  return 1 + extra.length
}

/**
 * Программа для окна с номером [index]: 1 — ВБ 1 (правки поверх рекомендованной), 2… — свои,
 * 0 — по возрасту без правок (график кончился). Пока добавленных ВБ нет, любой номер — ВБ 1:
 * так работало всегда, правки «Моего дня» действуют на все окна.
 */
export function programForSchedule(base: WakeProgram, custom: Phase[] | null, extra: WakeSchedule[], index: number): WakeProgram {
  const первое = custom === null ? base : { ...base, phases: custom }
  if (extra.length === 0 || index === 1) return первое
  if (index === 0) return base
  const своё = extra[index - 2]
  return своё === undefined ? base : { ...base, phases: своё.phases }
}

/** Номер ВБ для окна, перед которым сегодня уже было [windowsBefore] окон; 0 — график кончился. */
export function scheduleIndexFor(windowsBefore: number, extra: WakeSchedule[]): number {
  if (extra.length === 0) return 1
  const k = windowsBefore + 1
  return k <= scheduleCount(extra) ? k : 0
}

/** Начало текущих суток по режиму дня: сегодняшнее начало дня или вчерашнее, если ещё не наступило. */
export function currentDayStart(now: number, settings: DaySettings = DEFAULT_DAY): number {
  const сегодня = dayStartOf(now, settings)
  return now >= сегодня ? сегодня : nextDayStart(now, settings) - DAY
}

/**
 * Сколько окон уже начиналось в текущих сутках до момента [now] (само [now] не считается).
 *
 * Счёт идёт с утра — с пяти, если день по настройкам начинается позже. Подъём в шесть при
 * начале дня в семь приложение уже считает утренним окном, а не ночным пробуждением
 * (`isNightWake`), значит и в счёт окон дня он входит. Без этого такое окно выпадало, и все
 * следующие получали чужой ВБ: в 13:32 таймер шёл по ВБ 3 вместо ВБ 4 (31.10.2026).
 */
export function windowsToday(starts: number[], now: number, settings: DaySettings = DEFAULT_DAY): number {
  const от = currentDayStart(now, { ...settings, dayStartMin: Math.min(settings.dayStartMin, NIGHT_END_MIN) })
  return starts.filter((at) => at >= от && at < now).length
}

/** Имя по умолчанию для нового ВБ с номером [index]. */
export function defaultScheduleName(index: number, prefix: string): string {
  return prefix + ' ' + String(index)
}

export interface NapHint {
  /** Какой это сон за день по счёту: после ВБ 1 — первый. */
  index: number
  /** Сколько должен длиться этот сон по графику, минуты. */
  minutes: number
  /** До какого момента спать: уснул плюс длительность. */
  until: number
}

/**
 * Подсказка «сколько спать» для сна после окна с номером [finishedIndex].
 *
 * Не по остатку дня, а по графику: сон после ВБ k должен длиться столько, сколько записано
 * на карточке ВБ k+1 (решение заказчика 27.10.2026 — «нам нужно поспать 1:20, а не до
 * какого-то времени»). Момент «до» — уснул плюс длительность. После последнего ВБ — ночь,
 * подсказки нет; длительность не задана — тоже нет.
 */
export function napHint(asleepAt: number, finishedIndex: number, extra: WakeSchedule[]): NapHint | null {
  if (finishedIndex < 1) return null
  const следующее = extra[finishedIndex - 1]
  if (следующее === undefined) return null
  const minutes = следующее.napBefore
  if (minutes === undefined || minutes <= 0) return null
  return { index: finishedIndex, minutes, until: asleepAt + minutes * МИН }
}
